import { zodResolver } from "@hookform/resolvers/zod";
import { AxiosError } from "axios";
import clsx from "clsx";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { Password } from "primereact/password";
import type { ReactElement } from "react";
import { Controller, useForm, UseFormSetError } from "react-hook-form";
import { z } from "zod";

import { useSignupWithEmail } from "features/auth";
import toast from "lib/react-hot-toast";

const validationSchema = z.object({
  email: z.string().email(),
  password: z
    .string()
    .min(12, "Password must be at least 12 characters long.")
    .regex(/.*[A-Z]+.*/, {
      message: "Password must contain at least one uppercase letter.",
    })
    .regex(/.*\d+.*/, {
      message: "Password must contain at least one number.",
    }),
});

export type EmailSignupData = z.infer<typeof validationSchema>;

const defaultValues = {
  email: "",
  password: "",
};

function setFormErrors(error: AxiosError, setError: UseFormSetError<EmailSignupData>) {
  // @ts-expect-error - We have not properly defined the error response data for every case
  const errors = error.response?.data?.errors;
  if (!errors) {
    toast.error("Something went wrong. Please try again shortly or contact QuickBI support.");
  }

  for (const error of errors) {
    const sourcePointer = error.source.pointer;
    if (sourcePointer?.includes("email")) {
      setError("email", { type: "server", message: error.detail }, { shouldFocus: true });
    } else if (sourcePointer?.includes("password")) {
      setError("password", { type: "server", message: error.detail }, { shouldFocus: true });
    } else {
      setError("root", { type: "server", message: error.detail });
    }
  }
}

export function EmailSignupForm({ onSuccess }: { onSuccess: () => void }): ReactElement {
  const signupWithEmail = useSignupWithEmail();
  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<EmailSignupData>({
    defaultValues,
    resolver: zodResolver(validationSchema),
  });

  function onSubmit(data: EmailSignupData): void {
    signupWithEmail.mutate(data, {
      onSuccess: () => {
        toast.success("Signed up successfully!");
        onSuccess();
      },
      onError: (error) => {
        setFormErrors(error, setError);
      },
    });
  }

  function getFormErrorMessage(name: keyof EmailSignupData | "root"): ReactElement | undefined {
    if (!errors[name]) {
      return undefined;
    }
    if (name === "root") {
      return <Message className="mb-2" severity="error" content={errors.root?.message} />;
    }
    return <small className="p-error block">{errors[name]?.message}</small>;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-fluid text-left">
      <div className="field mb-4">
        <label htmlFor="email" className={clsx({ "p-error": errors.email })}>
          Email
        </label>
        <Controller
          name="email"
          control={control}
          render={({ field, fieldState }): ReactElement => (
            <InputText
              id={field.name}
              {...field}
              className={clsx("my-2 placeholder-gray-300", { "p-invalid": fieldState.invalid })}
            />
          )}
        />
        {getFormErrorMessage("email")}
      </div>
      <div className="field mb-4">
        <label htmlFor="password" className={clsx({ "p-error": errors.password })}>
          Password
        </label>
        <Controller
          name="password"
          control={control}
          render={({ field, fieldState }): ReactElement => (
            <Password
              id={field.name}
              {...field}
              feedback={false}
              toggleMask
              className={clsx("my-2 placeholder-gray-300", { "p-invalid": fieldState.invalid })}
            />
          )}
        />
        {getFormErrorMessage("password")}
        <small>
          Password should be at least 12 characters long, contain both lower and uppercase letters,
          and have at least one number.
        </small>
      </div>
      <Button
        type="submit"
        severity="secondary"
        size="small"
        label={signupWithEmail.isPending ? "Signing up..." : "Sign up"}
        className="p-button mt-2"
        disabled={signupWithEmail.isPending}
      />
    </form>
  );
}
